import { useEffect, useRef, useState } from "react";
import "./index.css";
import lesRogersScreenshot from "../../assets/images/portfolio/les-rogers-inc-home.webp";
import beYouScreenshot from "../../assets/images/portfolio/beyou-studio-23-salon-team.webp";
import pawsForwardScreenshot from "../../assets/images/portfolio/pawsforward-home.webp";
import averyJaneScreenshot from "../../assets/images/portfolio/averyjane-home.webp";

import wildSageScreenshot from "../../assets/images/portfolio/wildsage-home.webp";
import heavenlyEssenceScreenshot from "../../assets/images/portfolio/heavenly-essence-home.png";

type PortfolioItem = {
  name: string;
  url: string;
  screenshot: string;
};

const portfolioItems: PortfolioItem[] = [
  {
    name: "Heavenly Essence",
    url: "https://becomeheavenly.com",
    screenshot: heavenlyEssenceScreenshot,
  },
  {
    name: "Wild Sage Wellness",
    url: "https://wildsagewellnessnwa.com",
    screenshot: wildSageScreenshot,
  },
  {
    name: "Paws Forward Foundation",
    url: "https://pawsforwardnwa.org",
    screenshot: pawsForwardScreenshot,
  },
  {
    name: "Be You Studio 23 Salon",
    url: "https://beyoustudio23salon.com",
    screenshot: beYouScreenshot,
  },
  {
    name: "Les Rogers Inc.",
    url: "https://lesrogersinc.com",
    screenshot: lesRogersScreenshot,
  },
  {
    name: "AveryJaneDesigns.com",
    url: "https://averyjanedesigns.com",
    screenshot: averyJaneScreenshot,
  },
];

const portfolioFeatures = [
  {
    "title": "Modern design",
    "description": "Responsive, mobile-first websites built for your business."
  },
  {
    "title": "Membership and login",
    "description": "Allow your clients to join and log in to your site."
  },
  {
    "title": "Advanced chatbot avatars",
    "description": "Go beyond traditional chatbots with avatars that speak to your business using the information you provide.  We are AI certified and understand the importance of accurate information delivery."
  },
  {
    "title": "Email at your domain",
    "description": "Move away from public email providers with email hosting at your own domain.  Prevent your emails from going to your client's Spam folder !  We even work with Free Email Hosting providers"
  },
  {
    "title": "External integrations",
    "description": "GlossGenius, Zeffy, Stripe, and Square: a custom front door to your existing services, free yourself from template-based site limitations."
  },
  {
    "title": "Certified engineers",
    "description": "AWS and Anthropic certified, with engineering degrees and a deep understanding of the technology. We build and support your site without relying on drag-and-drop templates.  A website with Hogcloud is more than a site, it is an Application.  Integrate your business data and existing systems, connect with and provide new self-serving features for your customers"
  },
  {
    "title": "Fair Pricing",
    "description": "Don't fall into overpriced services - let us help you modernize at a fair, affordable rate"
  }
];

const Portfolio = () => {
  const [selectedFeature, setSelectedFeature] = useState<number | null>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const row = featuresRef.current;
    if (!row) return;
    const updateScroll = () => {
      setCanScrollLeft(row.scrollLeft > 1);
      setCanScrollRight(row.scrollLeft + row.clientWidth < row.scrollWidth - 1);
    };
    updateScroll();
    row.addEventListener('scroll', updateScroll, { passive: true });
    window.addEventListener('resize', updateScroll);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateScroll) : null;
    observer?.observe(row);
    return () => {
      row.removeEventListener('scroll', updateScroll);
      window.removeEventListener('resize', updateScroll);
      observer?.disconnect();
    };
  }, []);

  const scrollFeatures = (direction: number) => {
    const row = featuresRef.current;
    if (row) row.scrollBy({ left: direction * row.clientWidth * 0.75, behavior: 'auto' });
  };
  const feature = selectedFeature === null ? null : portfolioFeatures[selectedFeature];

  return (
  <div className="portfolio-page">
    <header className="portfolio-intro">
      <div className="portfolio-feature-layout">
        <div className="portfolio-feature-slider">
          <div className="portfolio-feature-toolbar">
            <p id="portfolio-scroll-hint">Swipe or use the arrows to explore</p>
            <div className="portfolio-feature-arrows">
              <button type="button" className="portfolio-scroll-button" aria-label="Scroll features left" aria-controls="portfolio-features" disabled={!canScrollLeft} onClick={() => scrollFeatures(-1)}>
                <span aria-hidden="true">←</span>
              </button>
              <button type="button" className="portfolio-scroll-button" aria-label="Scroll features right" aria-controls="portfolio-features" disabled={!canScrollRight} onClick={() => scrollFeatures(1)}>
                <span aria-hidden="true">→</span>
              </button>
            </div>
          </div>
        <div ref={featuresRef} id="portfolio-features" className="portfolio-features" role="region" tabIndex={0} aria-label="How we can help your business" aria-describedby="portfolio-scroll-hint">
          {portfolioFeatures.map((item, index) => (
            <button
              key={item.title}
              type="button"
              className="portfolio-feature-badge"
              aria-pressed={selectedFeature === index}
              aria-controls="portfolio-feature-detail"
              onClick={() => setSelectedFeature(index)}
            >
              {item.title}
            </button>
          ))}
        </div>
        </div>
        <div id="portfolio-feature-detail" className="portfolio-feature-detail" aria-live="polite" aria-atomic="true">
          {feature ? (
            <>
              <h2>{feature.title}</h2>
              <p>{feature.description}</p>
            </>
          ) : (
            <p>Click above for details on what we can offer, see below for satisfied clients</p>
          )}
        </div>
      </div>
    </header>

    <div className="portfolio-work-list" aria-label="Portfolio work samples">
      {portfolioItems.map((item) => (
        <article className="portfolio-card" key={item.name}>
          <div className="portfolio-card-details">
            <h2>{item.name}</h2>
          </div>
          <img
            className="portfolio-screenshot"
            src={item.screenshot}
            alt={`${item.name} website homepage`}
            loading="lazy"
            decoding="async"
          />
          <footer className="portfolio-card-footer">
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`See ${item.name} website (opens in a new tab)`}
            >
              See Website <span aria-hidden="true">↗</span>
            </a>
          </footer>
        </article>
      ))}
    </div>
  </div>
  );
};

export default Portfolio;
